/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryParams, ArrayParam, StringParam, withDefault } from 'use-query-params';
import { debounce } from 'lodash';
import CompanyNavBar, { navLinks } from '../../components/CompanyNavBar';
import saveFavourite from '../../lib/api/saveFavourite';
import { useAuth } from '../../commons/Auth';
import HumanResourcesFilters from '../../components/HumanResources/HumanResourcesFilters';
import { THumanResourcesFilters } from '../../components/HumanResources/human-resources';
import HumanResourcesList from '../../components/HumanResources/HumanResourcesList';
import useHumanResources from '../../lib/hooks/useHumanResources';
import useHumanResourceMutation from '../../lib/hooks/useHumanResourceMutation';
import unlockProfile from '../../lib/api/unlockProfile';
import useMe from '../../lib/hooks/useMe';
import FullScreenSpinner from '../../components/FullScreenSpinner';
import BannerJobPost from '../../components/BannerJobPost';
import SearchBar from '../../components/SearchBar';
import fetchConoscenzeInformatiche from '../../lib/api/fetchConoscenzeInformatiche';
import BannerBrandPromote from '../../components/BannerBrandPromote';
import {
  buildGeographicFilterUpdate,
  getActiveGeographicSearch,
  normalizeHumanResourcesFilters,
} from '../../lib/utils/humanResourcesFilters';

 export interface  ITypeConoscenzaInformatica {
    key: number,
     value: string,
  };

const Home: React.FC = function Home() {
  const { token }: any = useAuth();
  const [query, setQuery] = useQueryParams(
    {
      bucket: withDefault(StringParam, 'disponibilita'),
      eta: withDefault(StringParam, 'all'),
      titoloStudio: withDefault(StringParam, '0'),
      esperienza: withDefault(StringParam, 'all'),
      luogoLavoro: StringParam,
      residenza: withDefault(StringParam, undefined),
      provincia: withDefault(StringParam, undefined),
      occupazione: StringParam,
      mobilita: StringParam,
      qualifica: StringParam,
      incentivi: StringParam,
      categoriaProtetta: StringParam,
      automunito: StringParam,
      aree_di_competenza: withDefault(ArrayParam, []),
      conoscenze_informatiche: withDefault(ArrayParam, []),
      scheda_tecnica: withDefault(StringParam, ''),
    },
    { removeDefaultsFromUrl: true },
  );
  const DEFAULTS = useMemo(() => ({
    bucket: 'disponibilita',
    eta: 'all',
    titoloStudio: '0',
    esperienza: 'all',
    // gli altri li lasciamo rimossi (undefined) così si pulisce la URL
    luogoLavoro: undefined,
    residenza: undefined,
    provincia: undefined,
    occupazione: undefined,
    mobilita: undefined,
    qualifica: undefined,
    incentivi: undefined,
    categoriaProtetta: undefined,
    automunito: undefined,
    aree_di_competenza: [],
    conoscenze_informatiche: [],
    scheda_tecnica: undefined,
  }), []);
  
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [, setConoscenzeInformatiche] = useState<ITypeConoscenzaInformatica[]>([]);

  useMe(token);
  const filtersForHook = useMemo(
    () => normalizeHumanResourcesFilters(query as THumanResourcesFilters),
    [query],
  );
  const activeGeographicSearch = getActiveGeographicSearch(filtersForHook);

  const { data: users, isLoading } = useHumanResources(filtersForHook, token, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const { mutate: mutateFavourite } = useHumanResourceMutation(saveFavourite);
  const { mutate: mutateUnlockeds, isLoading: isLoadingUnlock } = useHumanResourceMutation(unlockProfile);

  useEffect(() => {
    fetchConoscenzeInformatiche().then((response) => {
      setConoscenzeInformatiche(response.map((x: any) => ({ key: x?.id, value: x?.attributes?.value })));
    });
  }, []);

  useEffect(() => {
    const normalizedProvince = query.provincia?.trim().toUpperCase();
    if (normalizedProvince && (query.residenza || query.provincia !== normalizedProvince)) {
      setQuery({ residenza: undefined, provincia: normalizedProvince }, 'replaceIn');
    }
  }, [query.provincia, query.residenza, setQuery]);

  const debouncedSetAdvancedFilters = useMemo(
    () =>
      debounce((partial: Partial<THumanResourcesFilters>) => {
        setQuery(partial as any, 'replaceIn');
      }, 200),
    [setQuery],
  );

  useEffect(() => () => debouncedSetAdvancedFilters.cancel(), [debouncedSetAdvancedFilters]);

  const resetFilters = useCallback(() => {
    debouncedSetAdvancedFilters.cancel();
    setQuery(DEFAULTS, 'replaceIn');
  }, [DEFAULTS, debouncedSetAdvancedFilters, setQuery]);

  return (
    <div>
      <CompanyNavBar navLink={navLinks.home} />
      <div>
        <BannerJobPost showBanner isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen} />
        <BannerBrandPromote showBanner isHome isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen} />
        <SearchBar
          activeField={activeGeographicSearch.field}
          activeValue={activeGeographicSearch.value}
          onModeChange={(nextField) => {
            if (nextField === 'provincia' && query.residenza) {
              setQuery({ residenza: undefined }, 'replaceIn');
            } else if (nextField === 'citta' && query.provincia) {
              setQuery({ provincia: undefined }, 'replaceIn');
            }
          }}
          onSearch={(field, value) => {
            setQuery(buildGeographicFilterUpdate(field, value), 'replaceIn');
          }}
          onReset={resetFilters}
        />

        {/* Filtri avanzati */}
        <HumanResourcesFilters
          filters={filtersForHook}
          onFilter={debouncedSetAdvancedFilters}
          onReset={resetFilters}
        />

        {(isLoading || isLoadingUnlock) && <FullScreenSpinner />}

        {!isLoading && !isLoadingUnlock && users && users.length > 0 ? (
          <HumanResourcesList
            users={users}
            onUnlock={(data: any) => mutateUnlockeds({ ...data, token })}
            onFavourite={(data: any) => mutateFavourite({ ...data, token })}
          />
        ) : (
          <div className="text-center mt-36">
            <h1>Nessun utente trovato</h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
